import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { SupabaseClient } from '../supabase/client';
import type { Store, UpdateStoreContactMethodsInput } from '../types/store';
import { updateStoreContactMethods } from '../services/update-store-contact-methods';

/**
 * F-007 — React Query mutation hook that wraps
 * `updateStoreContactMethods`. On success, writes the full
 * post-update store row into `['store', storeId]` via
 * `setQueryData` so the admin dashboard cache snaps to the new state
 * without a refetch.
 *
 * M1 cache-shape contract: `updateStoreContactMethods`'s `.select()`
 * list is synchronized with the `Store` type AND the admin
 * dashboard-route SELECT list, so the `setQueryData` write here cannot
 * silently truncate fields on a background refetch (F-004 §5.3).
 *
 * Invalidates ONLY the `['store', storeId]` key — never a broader
 * invalidation. Broader invalidation would touch unrelated caches (for
 * example F-005's storefront queries) and risk corrupting them.
 *
 * Takes `client` as a parameter so `packages/shared` stays decoupled
 * from any app's Supabase singleton (same pattern as `useAuthSession`).
 */
export function useUpdateStoreContactMethods(
  client: SupabaseClient,
): UseMutationResult<Store, unknown, UpdateStoreContactMethodsInput> {
  const queryClient = useQueryClient();

  return useMutation<Store, unknown, UpdateStoreContactMethodsInput>({
    mutationFn: (input) => updateStoreContactMethods(client, input),
    onSuccess: (row, variables) => {
      // M1 load-bearing — see F-004 §5.3. The service's SELECT list
      // matches the `Store` shape and the dashboard SELECT list
      // byte-for-byte, so this `setQueryData` cannot truncate fields
      // on a background refetch.
      queryClient.setQueryData(['store', variables.storeId], row);
    },
  });
}
